import { Platform, Text, View, Dimensions, StyleSheet } from 'react-native';
import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import Animated, { Extrapolate, SharedValue, FadeInRight, interpolate, useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import { SBItem } from '../../navigation/screens/MidLevel/DataScreen/components/SBItem';
import styles from '../../../stylesheet';
import { Pressable } from 'react-native';
import { Feather, MaterialIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

// Conditional import for carousel - only import on native platforms
let Carousel: any = null;

if (Platform.OS !== 'web') {
  try {
    const carouselModule = require("react-native-reanimated-carousel");
    Carousel = carouselModule.default;
  } catch (e) {
    console.warn('react-native-reanimated-carousel not available:', e);
  }
}

const PaginationItem: React.FC<{
  index: number;
  backgroundColor: string;
  length: number;
  animValue: SharedValue<number>;
  isRotate?: boolean;
}> = (props) => {
  const { animValue, index, length, backgroundColor, isRotate } = props;
  const theme = themeSelector();
  const width = 12;

  const animStyle = useAnimatedStyle(() => {
    let inputRange = [index - 1, index, index + 1];
    let outputRange = [-width, 0, width];

    if (index === 0 && animValue?.value > length - 1) {
      inputRange = [length - 1, length, length + 1];
      outputRange = [-width, 0, width];
    }

    return {
      transform: [
        {
          translateX: interpolate(
            animValue?.value,
            inputRange,
            outputRange,
            Extrapolate.CLAMP
          ),
        },
      ],
    };
  }, [animValue, index, length]);

  return (
    <View
      style={[modernStyles.paginationContainer, {
        backgroundColor: theme ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)',
      }]}
    >
      <Animated.View
        style={[
          modernStyles.paginationDot,
          {
            backgroundColor: backgroundColor || (theme ? '#6366f1' : '#8b5cf6'),
          },
          animStyle,
        ]}
      />
    </View>
  );
};

type GlobalTutorialTypes = {
  carouselData: Array<{ title: string, caption: string, image: JSX.Element }>,
  completeCallback?: Function,
  customHeight?: number,
  customWidth?: number,
  showArrows?: boolean,
  customStyle?: Object,
  loading?: boolean
}

const GlobalTutorial = ({ loading, showArrows, carouselData, completeCallback, customHeight, customWidth, customStyle, ...props }: GlobalTutorialTypes) => {
  const ProgressBarValue = useSharedValue<number>(0);
  const [canGoLeft, setCanGoLeft] = useState(false);
  const [canGoRight, setCanGoRight] = useState(true);
  const carousel = useRef<any>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = themeSelector();
  const viewCount = carouselData.length;
  const { width: screenWidth } = Dimensions.get('window');

  const generateColorsArray = (count: number, baseColor: string) => {
    const colors = [];
    const baseRGB = baseColor.match(/\d+/g);
    if (!baseRGB) return ['rgb(90,192,234)'];

    const baseBlue = parseInt(baseRGB[2]); // Extracting blue channel

    // Define the step for changing the intensity of the blue channel
    const blueStep = Math.floor(baseBlue / count);

    for (let i = 0; i < count; i++) {
      const newBlue = baseBlue - blueStep * i;
      const newColor = `rgb(${baseRGB[0]}, ${baseRGB[1]}, ${newBlue})`;
      colors.push(newColor);
    }

    return colors;
  };

  // Example usage:
  const numberOfColors = carouselData.length; // Set the desired number of colors
  const colors = generateColorsArray(numberOfColors, 'rgb(90,192,234)');

  const _handleCallback = useCallback(() => {
    if (completeCallback) {
      completeCallback();
    }
  }, [completeCallback]);

  useEffect(() => {
    // Handle any side effects here
  }, [currentIndex]);

  const _handleCanGoBack = () => {
    if (currentIndex <= 0) {
      return true
    } else return false
  }

  const _handleCanGoForward = () => {
    if (currentIndex == carouselData.length - 1) {
      return true
    } else return false
  }

  // Modern Web carousel component with glassmorphism styling
  const WebCarousel = () => {
    return (
      <Animatable.View
        animation="zoomIn"
        duration={800}
        style={[modernStyles.webCarouselContainer, {
          backgroundColor: theme ? "rgba(255, 255, 255, 0.95)" : "rgba(30, 41, 59, 0.95)",
          borderColor: theme ? "rgba(0, 0, 0, 0.06)" : "rgba(255, 255, 255, 0.1)",
          width: customWidth || 320,
          height: customHeight || 400,
        }]}
      >
        <View style={modernStyles.carouselContent}>
          {/* Header with step indicator */}
          <View style={modernStyles.headerSection}>
            <View style={[modernStyles.stepIndicator, {
              backgroundColor: theme ? "#ddd6fe" : "#312e81"
            }]}>
              <MaterialIcons
                name="help-outline"
                size={20}
                color={theme ? "#7c3aed" : "#a78bfa"}
              />
            </View>
            <View style={modernStyles.stepInfo}>
              <Text style={[modernStyles.stepCounter, {
                color: theme ? "#64748b" : "#94a3b8"
              }]}>
                {currentIndex + 1} of {carouselData.length}
              </Text>
              <View style={modernStyles.progressBar}>
                <View style={[modernStyles.progressFill, {
                  backgroundColor: theme ? "#6366f1" : "#8b5cf6",
                  width: `${((currentIndex + 1) / carouselData.length) * 100}%`
                }]} />
              </View>
            </View>
          </View>

          {/* Title */}
          <Text style={[modernStyles.carouselTitle, {
            color: theme ? "#1e293b" : "#f1f5f9"
          }]}>
            {carouselData[currentIndex]?.title}
          </Text>

          {/* Image/Content Section */}
          <View style={modernStyles.imageSection}>
            {carouselData[currentIndex]?.image}
          </View>

          {/* Caption */}
          <Text style={[modernStyles.carouselCaption, {
            color: theme ? "#64748b" : "#94a3b8"
          }]}>
            {carouselData[currentIndex]?.caption}
          </Text>
        </View>

        {/* Modern Pagination Dots */}
        

       
      </Animatable.View>
    );
  };

  const goToPrevious = () => {
    if (Platform.OS === 'web') {
      setCurrentIndex(prev => prev > 0 ? prev - 1 : carouselData.length - 1);
    } else {
      carousel.current?.prev();
    }
  };

  const goToNext = () => {
    if (Platform.OS === 'web') {
      setCurrentIndex(prev => prev < carouselData.length - 1 ? prev + 1 : 0);
    } else {
      carousel.current?.next();
    }
  };

  // Auto-advance for web
  useEffect(() => {
    if (Platform.OS === 'web') {
      const interval = setInterval(() => {
        setCurrentIndex(prev => prev < carouselData.length - 1 ? prev + 1 : 0);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [carouselData.length]);

  return (
    <Animatable.View
      animation="fadeIn"
      duration={600}
      style={[modernStyles.mainContainer, {
        minHeight: customHeight || 350,
        minWidth: 320,
        ...(customStyle as any)
      }]}
    >
      <View style={modernStyles.carouselWrapper}>
        {showArrows && (
          <Animatable.View
            animation="slideInLeft"
            delay={300}
          >
            <Pressable
              onPress={goToPrevious}
              disabled={Platform.OS !== 'web' && _handleCanGoBack()}
              style={[modernStyles.arrowButton, modernStyles.leftArrow, {
                backgroundColor: theme ? "rgba(255, 255, 255, 0.9)" : "rgba(30, 41, 59, 0.9)",
                borderColor: theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.2)",
                opacity: (Platform.OS !== 'web' && _handleCanGoBack()) ? 0.4 : 1,
              }]}
            >
              <Feather
                name="chevron-left"
                size={24}
                color={(Platform.OS !== 'web' && _handleCanGoBack()) 
                  ? (theme ? "#94a3b8" : "#64748b") 
                  : (theme ? "#6366f1" : "#8b5cf6")
                }
              />
            </Pressable>
          </Animatable.View>
        )}

        {Platform.OS === 'web' ? (
          <WebCarousel />
        ) : (
          Carousel && (
            <Animatable.View
              animation="zoomIn"
              delay={200}
              style={modernStyles.nativeCarouselContainer}
            >
              <Carousel
                style={[modernStyles.nativeCarousel, {
                  minHeight: customHeight || 350,
                  minWidth: customWidth || 320,
                }]}
                width={customWidth || 320}
                height={customHeight || 400}
                ref={carousel}
                autoPlayInterval={6000}
                loop={true}
                scrollAnimationDuration={800}
                autoPlay={true}
                onSnapToItem={(index: number) => { setCurrentIndex(index) }}
                data={colors}
                renderItem={({ index, animationValue }: any) => {
                  return (
                    <SBItem
                      index={index}
                      key={index}
                      data={carouselData}
                      animationValue={animationValue}
                    />
                  )
                }}
              />
            </Animatable.View>
          )
        )}

        {showArrows && (
          <Animatable.View
            animation="slideInRight"
            delay={300}
          >
            <Pressable
              onPress={goToNext}
              disabled={Platform.OS !== 'web' && _handleCanGoForward()}
              style={[modernStyles.arrowButton, modernStyles.rightArrow, {
                backgroundColor: theme ? "rgba(255, 255, 255, 0.9)" : "rgba(30, 41, 59, 0.9)",
                borderColor: theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.2)",
                opacity: (Platform.OS !== 'web' && _handleCanGoForward()) ? 0.4 : 1,
              }]}
            >
              <Feather
                name="chevron-right"
                size={24}
                color={(Platform.OS !== 'web' && _handleCanGoForward()) 
                  ? (theme ? "#94a3b8" : "#64748b") 
                  : (theme ? "#6366f1" : "#8b5cf6")
                }
              />
            </Pressable>
          </Animatable.View>
        )}
      </View>
    </Animatable.View>
  );
}

const modernStyles = StyleSheet.create({
  // Pagination Styles
  paginationContainer: {
    width: 12,
    height: 12,
    borderRadius: 6,
    overflow: 'hidden',
  },

  paginationDot: {
    borderRadius: 6,
    flex: 1,
  },

  // Main Container
  mainContainer: {
    flex: 1,
    alignSelf: 'stretch',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
  },

  carouselWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
  },

  // Web Carousel Styles
  webCarouselContainer: {
    borderRadius: 24,
    padding: 24,
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    elevation: 12,
    borderWidth: 1,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(20px)',
      }
    })
  },

  carouselContent: {
    flex: 1,
    justifyContent: 'space-between',
    gap: 20,
  },

  headerSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },

  stepIndicator: {
    width: 40,
    height: 40,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },

  stepInfo: {
    flex: 1,
    gap: 6,
  },

  stepCounter: {
    fontSize: 12,
    fontWeight: '600',
  },

  progressBar: {
    height: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 2,
    overflow: 'hidden',
  },

  progressFill: {
    height: '100%',
    borderRadius: 2,
  },

  carouselTitle: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 32,
  },

  imageSection: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 120,
  },

  carouselCaption: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    fontWeight: '500',
  },

  paginationSection: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    marginVertical: 16,
  },

  modernPaginationDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },

  actionSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },

  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    gap: 8,
    minWidth: 100,
  },

  secondaryButton: {
    borderWidth: 1,
  },

  primaryButton: {
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 4,
  },

  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },

  primaryActionText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },

  // Arrow Buttons
  arrowButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 6,
    borderWidth: 1,
  },

  leftArrow: {
    marginRight: 8,
  },

  rightArrow: {
    marginLeft: 8,
  },

  // Native Carousel Styles
  nativeCarouselContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },

  nativeCarousel: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
});

export default memo(GlobalTutorial);